
import { useState, useEffect } from 'react';
import { skipWaiting } from '@/lib/sw-register';
import { cn } from '@/lib/utils';
import { useBranding } from '@/lib/branding-context';

/**
 * Component that shows when a service worker update is available
 * Prompts user to refresh the page
 */
export function UpdatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const { branding } = useBranding();

  useEffect(() => {
    // Listen for service worker update available event
    const handleUpdateAvailable = (event: CustomEvent<{ registration: ServiceWorkerRegistration }>) => {
      setRegistration(event.detail.registration);
      setShowPrompt(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable as EventListener);

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable as EventListener);
    };
  }, []);

  const handleUpdate = () => {
    if (registration) {
      skipWaiting(registration);
      // Reload after the new service worker takes control
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50',
        'max-w-sm w-full',
        'animate-slide-up'
      )}
    >
      <div
        className={cn(
          'bg-white dark:bg-slate-800',
          'rounded-lg shadow-lg',
          'border border-slate-200 dark:border-slate-700',
          'p-4'
        )}
      >
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'flex-shrink-0',
              'w-10 h-10 rounded-full',
              'bg-primary-100 dark:bg-primary-900/30',
              'flex items-center justify-center'
            )}
          >
            <svg
              className="w-5 h-5 text-primary-600 dark:text-primary-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </span>

          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
              Update Available
            </h4>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              A new version of {branding.appName} is available. Refresh to update.
            </p>

            <div className="mt-3 flex gap-2">
              <button
                onClick={handleUpdate}
                className={cn(
                  'px-3 py-1.5 rounded-md',
                  'text-sm font-medium',
                  'bg-primary-600 text-white',
                  'hover:bg-primary-700',
                  'transition-colors'
                )}
              >
                Refresh Now
              </button>
              <button
                onClick={handleDismiss}
                className={cn(
                  'px-3 py-1.5 rounded-md',
                  'text-sm font-medium',
                  'text-slate-600 dark:text-slate-400',
                  'hover:bg-slate-100 dark:hover:bg-slate-700',
                  'transition-colors'
                )}
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpdatePrompt;
